// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import ConfirmMergeModal from 'app/common/react/components/profile/ProfileBoards/ConfirmMergeModal';
import DragScrollManager from 'app/common/react/components/DragDrop/DragScrollManager';
import errorUtil from 'app/common/lib/errorUtil';
import i18n from 'app/common/lib/i18n';
import MergeErrorModal from 'app/common/react/components/profile/ProfileBoards/MergeErrorModal';
import OverwriteWarningModal from 'app/common/react/components/profile/ProfileBoards/OverwriteWarningModal';
import ProfileBoardCardContainer from 'app/common/react/components/profile/ProfileBoardCard/ProfileBoardCardContainer';
import ProfileBoardCardWrapper from 'app/common/react/components/profile/ProfileBoards/ProfileBoardCardWrapper';
import ProfileBoardCreateCard from 'app/common/react/components/profile/ProfileBoardCard/ProfileBoardCreateCard';
import ProfileBoardInviteCardContainer from 'app/common/react/components/profile/ProfileBoardCard/ProfileBoardInviteCardContainer';
import ProfileBoardsListHeader from 'app/common/react/components/profile/ProfileBoards/ProfileBoardsListHeader';
import ProfileGridLayout from 'app/common/react/components/profile/ProfileGridLayout/ProfileGridLayout';
import reorderProfileBoards from 'app/common/redux/actions/feeds/reorderProfileBoards';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import stopProfileTiming from 'app/common/react/components/profile/ProfileBoards/utils/stopProfileTiming';
import type { BoardGroup } from 'app/common/react/components/profile/ProfileBoards/BoardGroup';
import type { BoardSortOptionsType } from 'app/packages/pinterest-flow-types/user';
import type { DesktopProfileBoard } from 'app/packages/pinterest-flow-types/board';
import withDragDropSurface from 'app/common/lib/react/withDragDropSurface';
import withResource from 'app/common/lib/react/withResource';
import withShowBlockingActionToast from 'app/common/react/components/save/board/withShowBlockingActionToast';
import { Box } from 'gestalt';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { getBoardsContent } from 'app/common/redux/selectors/board';
import { logCustomTraceEvent } from 'app/common/lib/TraceLogger/logTraceEvent';
import { mergeBoards } from 'app/common/redux/actions/boards';
import { reorderItems } from 'app/common/redux/reducers/feeds';
import { track } from 'app/common/lib/react/analytics';
import { type State as ReduxBoard } from 'app/common/redux/reducers/board';
import { updateBoardSortOption } from 'app/common/redux/actions/views';
import DraggableItem, {
  DragItemTypes,
  type HoverRelativePosition,
} from 'app/common/react/components/DragDrop/DraggableItem';
import type { Invite, InviteResourceType } from 'app/common/react/components/BoardCollab/types';
import {
  getCardWidth,
  type ProfileBoardCardLayout,
} from 'app/common/react/components/profile/ProfileBoardCard/layout';
import {
  getTargetIndex,
  hoverLocationsAreEqual,
  getHoverRegionWithinItem,
  type HoverLocation,
} from 'app/common/react/components/DragDrop/dragHoverUtils';

type Props = {|
  boardIds: Array<string>,
  boardInvites: InviteResourceType,
  boardMap: { [string]: ReduxBoard },
  boardOrder?: BoardSortOptionsType,
  connectDropTarget?: Function,
  dispatch: Function,
  grouping: BoardGroup,
  hideCreateBoardCard?: boolean,
  isDragWithinBounds?: boolean,
  isOverview: boolean,
  isOwnProfile: boolean,
  isUserBlocked: boolean,
  layout: 'list' | ProfileBoardCardLayout,
  onBoardArchiveChange?: () => void,
  onBoardCreate: (board: DesktopProfileBoard) => void,
  onBoardDelete: (boardId: string) => void,
  onBoardLeave?: (boardId: string) => void,
  onBoardPrivacyChange?: () => void,
  onBoardReorder?: () => void,
  onBoardUpdate?: (id: string, board: DesktopProfileBoard) => void,
  reorderBoards: (sourceIndex: number, targetIndex: number) => void,
  shouldShowBoardCreateCard?: boolean,
  showBlockingActionToast: Function,
  showCreatorProfile: boolean,
|};

type State = {|
  activeModal: ?React.Node,
  boardIds: Array<string>,
  dropAction: ?('merge' | 'reorder'),
  previewIndex: number,
  sourceIndex: number,
  targetIndex: number,
|};

const UNSELECTED_INDEX = -1;
const BOARD_REORDER_DELAY = 200;
const BOARD_MERGE_DELAY = 20;

export class ProfileBoardsList extends React.PureComponent<Props, State> {
  state = {
    activeModal: null,
    boardIds: [...this.props.boardIds],
    dropAction: null,
    previewIndex: UNSELECTED_INDEX,
    sourceIndex: UNSELECTED_INDEX,
    targetIndex: UNSELECTED_INDEX,
  };

  componentDidMount() {
    const { boardMap, boardIds, isOwnProfile, grouping, showCreatorProfile } = this.props;
    if (grouping === 'unlabeled') {
      logCustomTraceEvent({ name: 'PublicBoardsListMountedTraceEvent' });
    } else if (grouping === 'secret') {
      logCustomTraceEvent({ name: 'SecretBoardsListMountedTraceEvent' });
    }

    logCustomTraceEvent({ name: 'MasonryDidMount' });

    if (this.container) {
      logCustomTraceEvent({
        name: 'UserNavRegisterGridImagesTraceEvent',
        viewportFilled: false,
        gridData: boardIds.map((boardId) => boardMap[boardId]).filter((board) => !!board),
        el: this.container.current,
      });
    }

    logCustomTraceEvent({ name: 'UserNavRegisterGridItemsPositionedTraceEvent' });
    stopProfileTiming({ isOwnProfile, showCreatorProfile });
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.boardIds.join(',') !== this.props.boardIds.join(',')) {
      this.setState({
        boardIds: [...nextProps.boardIds],
        dropAction: null,
        previewIndex: UNSELECTED_INDEX,
        sourceIndex: UNSELECTED_INDEX,
        targetIndex: UNSELECTED_INDEX,
      });
    }
  }

  componentWillUnmount() {
    this.cancelHoverTimeout();
  }

  get allowDragging(): boolean {
    const { isOwnProfile, isOverview } = this.props;
    return isOwnProfile && !isOverview;
  }

  container = React.createRef<HTMLElement>();

  cancelHoverTimeout = () => {
    const dragHoverTimeout = this.dragHoverTimeout;
    if (dragHoverTimeout) {
      clearTimeout(dragHoverTimeout.timeoutId);
    }
    this.dragHoverTimeout = null;
  };

  dragHoverTimeout: ?{| hoverLocation: HoverLocation, timeoutId: TimeoutID |} = null;

  isDragging = () => this.state.sourceIndex !== UNSELECTED_INDEX;

  handleBoardCardDragBegin = (index: number) =>
    this.setState({
      previewIndex: index,
      sourceIndex: index,
    });

  handleBoardCardDragHover = (
    hoverIndex: number,
    hoverRelativePosition: ?HoverRelativePosition,
  ) => {
    if (!hoverRelativePosition) {
      return;
    }

    const { boardIds, previewIndex } = this.state;

    if (!this.allowDragging) {
      return;
    }

    const hoverRegion = this.allowDragging
      ? getHoverRegionWithinItem({
          hoverRelativePosition,
          centerEnabled: hoverIndex !== previewIndex,
        })
      : 'center';
    const hoverLocation = {
      itemIndex: hoverIndex,
      regionWithinItem: hoverRegion,
    };

    if (
      this.dragHoverTimeout &&
      hoverLocationsAreEqual(hoverLocation, this.dragHoverTimeout.hoverLocation)
    ) {
      return; // No-op since delayed hover handler is already registered
    }

    this.cancelHoverTimeout(); // cancel previous delayed hover timeout since hover location has been changed

    const dropAction = hoverRegion === 'center' ? 'merge' : 'reorder';
    const targetIndex = getTargetIndex({
      hoverLocation,
      sourceIndex: previewIndex,
    });

    if (targetIndex === previewIndex) {
      if (targetIndex !== this.state.targetIndex) {
        this.setState({ targetIndex });
      }
      return;
    }

    this.dragHoverTimeout = {
      hoverLocation,
      timeoutId: setTimeout(() => {
        this.dragHoverTimeout = null;
        this.setState({
          dropAction,
          targetIndex,
          ...(dropAction === 'reorder'
            ? {
                previewIndex: targetIndex,
                boardIds: reorderItems(boardIds, previewIndex, targetIndex),
              }
            : {}),
        });
      }, dropAction === 'reorder' ? BOARD_REORDER_DELAY : BOARD_MERGE_DELAY),
    };
  };

  handleBoardInvitesDidUpdate = () => {
    this.props.boardInvites.refreshData();
  };

  handleCloseModal = () => this.setState({ activeModal: null });

  handleBoardCardDragEnd = (didDrop: boolean) => {
    const {
      boardIds: originalBoardIds,
      boardMap,
      boardOrder,
      dispatch,
      onBoardReorder,
    } = this.props;
    const { boardIds, dropAction, previewIndex, sourceIndex, targetIndex } = this.state;
    this.cancelHoverTimeout();

    this.setState({
      boardIds: originalBoardIds,
      previewIndex: UNSELECTED_INDEX,
      sourceIndex: UNSELECTED_INDEX,
      targetIndex: UNSELECTED_INDEX,
    });

    if (
      !didDrop ||
      sourceIndex === UNSELECTED_INDEX ||
      targetIndex === UNSELECTED_INDEX ||
      sourceIndex === targetIndex
    ) {
      return;
    }

    // If a user tries to drag and drop from a non-drag+drop state
    if (boardOrder !== 'custom') {
      this.setState({
        activeModal: (
          <OverwriteWarningModal
            onConfirm={this.handleOverwriteBoardOrder}
            onHide={this.handleCloseModal}
          />
        ),
      });
    }

    if (dropAction === 'merge') {
      // merge from sourceIndex to targetIndex
      const indexToBoard = (index) => {
        const boardId = boardIds[index];
        return boardId && boardMap[boardId];
      };
      const sourceBoard = indexToBoard(previewIndex);
      const targetBoard = indexToBoard(targetIndex);
      if (sourceBoard && targetBoard) {
        if (sourceBoard.collaborated_by_me) {
          this.setState({ activeModal: <MergeErrorModal onHide={this.handleCloseModal} /> });
          return;
        }
        this.setState({
          activeModal: (
            <ConfirmMergeModal
              onConfirm={() => this.handleMergeBoards(sourceBoard, targetBoard)}
              onHide={this.handleCloseModal}
              sourceBoard={sourceBoard}
              targetBoard={targetBoard}
            />
          ),
        });
      }
    } else if (
      targetIndex < boardIds.length &&
      sourceIndex < boardIds.length &&
      boardOrder === 'custom'
    ) {
      dispatch(reorderProfileBoards({ boardIds: originalBoardIds, sourceIndex, targetIndex }));
      if (onBoardReorder) {
        onBoardReorder();
      }
    }
  };

  handleOverwriteBoardOrder = () => {
    const { dispatch } = this.props;
    const newBoardOrder = 'custom';
    dispatch(updateBoardSortOption(newBoardOrder));

    ResourceFactory.create('UserMetadataResource', {
      most_recent_board_sort_order: 'custom',
    }).callUpdate();
    this.handleCloseModal();
  };

  handleMergeBoards = (sourceBoard: DesktopProfileBoard, targetBoard: DesktopProfileBoard) => {
    const { dispatch, showBlockingActionToast, onBoardDelete } = this.props;
    track(PCONST.thrift.EventType.BOARD_MERGE, {
      objectId: sourceBoard.id,
      merge_flow: 'dnd',
    });

    dispatch(mergeBoards(sourceBoard.id, targetBoard.id))
      .then(() => showBlockingActionToast({ actionType: 'merge', board: targetBoard }))
      .catch((error) => {
        errorUtil.showError(
          error.message ||
            i18n._('Failed to merge boards', 'Error message when failed to merge boards'),
        );
      });
    onBoardDelete(sourceBoard.id); // optimistically remove the board from redux
    this.handleCloseModal();
  };

  renderBoards = () => {
    const {
      boardOrder,
      boardMap,
      boardInvites,
      isOwnProfile,
      isDragWithinBounds,
      onBoardArchiveChange,
      onBoardDelete,
      onBoardPrivacyChange,
      onBoardUpdate,
      onBoardCreate,
      onBoardLeave,
      grouping,
      layout,
      isUserBlocked,
      shouldShowBoardCreateCard,
    } = this.props;
    const { activeModal, boardIds, dropAction, targetIndex } = this.state;
    const boardRepContainerWidth = layout === 'list' ? '100%' : getCardWidth(layout) + 24;
    const inListView = layout === 'list';
    const requests = (boardInvites.data || []).slice(0, 5);
    const showInvites = isOwnProfile && !inListView && requests.length > 0;

    return (
      <React.Fragment>
        <ProfileGridLayout
          {...(inListView ? {} : { itemWidth: boardRepContainerWidth })}
          maxColumns={inListView ? 0 : 12}
        >
          {shouldShowBoardCreateCard && (
            <Box paddingX={3} paddingY={5}>
              <ProfileBoardCreateCard
                layout={layout}
                onBoardCreate={onBoardCreate}
                privacy={grouping === 'unlabeled' ? 'public' : grouping}
              />
            </Box>
          )}
          {showInvites &&
            requests.map((invite: Invite, index: number) => {
              const boardNode = (
                <ProfileBoardInviteCardContainer
                  invite={invite}
                  layout={layout}
                  onDeclineRequest={this.handleBoardInvitesDidUpdate}
                  slotIndex={index}
                />
              );
              return (
                <ProfileBoardCardWrapper
                  key={invite.board.id}
                  containerWidth={boardRepContainerWidth}
                  inListView={inListView}
                >
                  {boardNode}
                </ProfileBoardCardWrapper>
              );
            })}
          {boardIds
            .map((boardId) => boardMap[boardId])
            .filter((board) => !!board)
            .map((board: ReduxBoard, index: number) => {
              const boardNode = (
                <ProfileBoardCardContainer
                  board={board}
                  boardOrder={boardOrder}
                  isOwnProfile={isOwnProfile}
                  isMergeTarget={dropAction === 'merge' && index === targetIndex}
                  isReordering={this.isDragging()}
                  layout={layout}
                  onBoardArchiveChange={onBoardArchiveChange}
                  onBoardDelete={() => onBoardDelete(board.id)}
                  onBoardPrivacyChange={onBoardPrivacyChange}
                  onBoardLeave={onBoardLeave}
                  onBoardUpdate={onBoardUpdate}
                  slotIndex={index}
                  isUserBlocked={isUserBlocked}
                />
              );
              return (
                <ProfileBoardCardWrapper
                  key={board.id}
                  containerWidth={boardRepContainerWidth}
                  inListView={inListView}
                  semiTransparent={this.isDragging() && !isDragWithinBounds}
                >
                  {this.allowDragging ? (
                    <DraggableItem
                      index={index}
                      onDragBegin={this.handleBoardCardDragBegin}
                      onDragHover={this.handleBoardCardDragHover}
                      onDragEnd={this.handleBoardCardDragEnd}
                      type={DragItemTypes.Board}
                    >
                      {boardNode}
                    </DraggableItem>
                  ) : (
                    boardNode
                  )}
                </ProfileBoardCardWrapper>
              );
            })}
        </ProfileGridLayout>
        {activeModal}
      </React.Fragment>
    );
  };

  render() {
    const { connectDropTarget, boardOrder, layout } = this.props;
    return (
      <Box ref={this.container}>
        {layout === 'list' && (
          <ProfileBoardsListHeader
            headerLabel={
              ['newest', 'oldest'].includes(boardOrder)
                ? i18n._('Created on', 'Profile board list view heading label')
                : i18n._('Last saved to', 'Profile board list view heading label')
            }
          />
        )}
        {this.allowDragging && connectDropTarget ? (
          <React.Fragment>
            {connectDropTarget(
              <div>
                {/* `<div>` is needed because React DnD only accepts native element nodes */}
                {this.renderBoards()}
              </div>,
            )}
            {this.isDragging() && <DragScrollManager />}
          </React.Fragment>
        ) : (
          this.renderBoards()
        )}
      </Box>
    );
  }
}

export default compose(
  withDragDropSurface(DragItemTypes.Board),
  withShowBlockingActionToast,
  connect(
    createStructuredSelector({
      boardMap: getBoardsContent,
    }),
  ),
  withResource(
    {
      name: 'BoardInvitesResource',
      key: 'boardInvites',
      options: (props) => ({
        current_user: true,
        field_set_key: 'news',
      }),
    },
    { allowStale: false },
  ),
)(ProfileBoardsList);
